/** CN Gate Appointment time windows (start label → stored range). */
export const CN_TIME_SLOTS: { start: string; window: string }[] = [
  { start: '00:00', window: '00:00-03:59' },
  { start: '04:00', window: '04:00-04:59' },
  { start: '05:00', window: '05:00-05:59' },
  { start: '06:00', window: '06:00-06:59' },
  { start: '07:00', window: '07:00-07:59' },
  { start: '08:00', window: '08:00-08:59' },
  { start: '09:00', window: '09:00-09:59' },
  { start: '10:00', window: '10:00-10:59' },
  { start: '11:00', window: '11:00-11:59' },
  { start: '12:00', window: '12:00-12:59' },
  { start: '13:00', window: '13:00-13:59' },
  { start: '14:00', window: '14:00-15:59' },
  { start: '16:00', window: '16:00-17:59' },
  { start: '18:00', window: '18:00-18:59' },
  { start: '19:00', window: '19:00-19:59' },
  { start: '20:00', window: '20:00-23:59' },
]

export const CN_PICKER_STARTS = CN_TIME_SLOTS.map((s) => s.start)

const START_TO_WINDOW = Object.fromEntries(CN_TIME_SLOTS.map((s) => [s.start, s.window]))

const WINDOW_TO_START = Object.fromEntries(CN_TIME_SLOTS.map((s) => [s.window, s.start]))

/** First HH:mm from a CN range e.g. "16:00-17:59" → "16:00" */
export function firstClockFromTimeSlot(timeSlot: string | null | undefined): string | null {
  if (!timeSlot) return null
  const normalized = timeSlot.replace(/\s*-\s*/g, '-').trim()
  if (WINDOW_TO_START[normalized]) return WINDOW_TO_START[normalized]
  const beforeDash = normalized.split('-')[0]?.trim() ?? ''
  const m = beforeDash.match(/^(\d{1,2}):(\d{2})/)
  if (!m) return null
  return `${m[1].padStart(2, '0')}:${m[2].padStart(2, '0')}`
}

/** Picker start (e.g. 16:00) → DB value (16:00-17:59). No separate 17:00 slot. */
export function startToCnWindow(start: string): string {
  return START_TO_WINDOW[start] ?? start
}

/** Nearest CN picker start for an appointment hour. */
export function snapToPickerStart(apptHour: number): string {
  const starts = CN_PICKER_STARTS.map((s) => {
    const [h] = s.split(':').map(Number)
    return { start: s, h: h ?? 0 }
  })
  let best = starts[0]!
  for (const s of starts) {
    if (apptHour >= s.h) best = s
  }
  return best.start
}

export function snapStartFromRow(timeSlotRaw: string, apptHour: number): string {
  const fromSlot = firstClockFromTimeSlot(timeSlotRaw === '—' ? null : timeSlotRaw)
  if (fromSlot && CN_PICKER_STARTS.includes(fromSlot)) return fromSlot
  return snapToPickerStart(apptHour)
}

export function sortPickerStarts(starts: string[]): string[] {
  return [...starts].sort((a, b) => CN_PICKER_STARTS.indexOf(a) - CN_PICKER_STARTS.indexOf(b))
}

/** Parse stored value (single or comma-separated windows) into picker start times. */
export function parsePickerStartsFromStored(timeSlotRaw: string | null | undefined): string[] {
  if (!timeSlotRaw || timeSlotRaw === '—') return []
  const parts = timeSlotRaw.split(',').map((p) => p.trim()).filter(Boolean)
  const out: string[] = []
  for (const part of parts) {
    const start = firstClockFromTimeSlot(part)
    if (start && CN_PICKER_STARTS.includes(start) && !out.includes(start)) {
      out.push(start)
    }
  }
  return sortPickerStarts(out)
}

export function startsToStoredSlots(starts: string[]): string {
  return sortPickerStarts(starts).map(startToCnWindow).join(',')
}

export function formatSlotsForDisplay(timeSlotRaw: string): string {
  const starts = parsePickerStartsFromStored(timeSlotRaw === '—' ? null : timeSlotRaw)
  if (starts.length > 0) return starts.join(', ')
  const one = firstClockFromTimeSlot(timeSlotRaw === '—' ? null : timeSlotRaw)
  return one ?? '—'
}

export function sameStartSets(a: string[], b: string[]): boolean {
  const sa = sortPickerStarts(a)
  const sb = sortPickerStarts(b)
  if (sa.length !== sb.length) return false
  return sa.every((v, i) => v === sb[i])
}
